import { ColumnConfiguration } from '@interfaces/models';
import { useState, useMemo } from 'react';
import { useDebounce } from '@ui/hooks';

export type DynamicFiltersState = Record<string, string[] | string>;

export const useDynamicFilters = ({ columns }: { columns: ColumnConfiguration[] }) => {
  const [filtersState, setFiltersState] = useState<DynamicFiltersState>({} as DynamicFiltersState);
  const debouncedFiltersState = useDebounce(filtersState, 500);

  const selectedFilters = useMemo(() => {
    if (!filtersState) return 0;
    return Object.entries(filtersState).filter(([_key, value]) => value !== '');
  }, [filtersState]);

  const filters = useMemo(() => {
    const columnsWithFilters = columns.filter((column) => column.filters);
    const filters = columnsWithFilters.flatMap((column) => column.filters!);

    return filters;
  }, [columns]);

  const onSelectChange = (id: string, value: string) => {
    setFiltersState((prev) => ({
      ...prev,
      [id]: value,
    }));
  };

  const onMultiSelectChange = (id: string, values: string[]) => {
    setFiltersState((prev) => ({
      ...prev,
      [id]: values,
    }));
  };

  const onInputChange = (id: string, value: string) => {
    setFiltersState((prev) => ({
      ...prev,
      [id]: value,
    }));
  };

  return {
    debouncedFiltersState,
    selectedFilters,
    filtersState,
    filters,
    clearFilters: () => setFiltersState({}),
    onMultiSelectChange,
    setFiltersState,
    onSelectChange,
    onInputChange,
  };
};
